/**
 * The contents of a bar, read back out of a saved file.
 *
 * Nothing here repairs anything. A note whose duration is a type the editor does not know
 * is a refusal rather than a quietly substituted crotchet, because a file that silently
 * changes when it is opened is worse than a file that will not open: the first loses the
 * music without saying so.
 */

import { NOTE_TYPES } from "../document/duration";
import type { Duration, TupletRatio } from "../document/duration";
import { STEPS } from "../document/pitch";
import type { NotePitch, Transposition } from "../document/pitch";
import type { TabPosition } from "../document/tab";
import { FIGURE_MARKS } from "../document/figures";
import type { Figure, FigureMark, FiguredBass } from "../document/figures";
import { HARMONY_KINDS } from "../document/harmony";
import type { ChordRoot, Harmony } from "../document/harmony";
import type {
  Arpeggio,
  Articulation,
  Clef,
  Direction,
  InstrumentChange,
  FermataShape,
  Lyric,
  MusicEvent,
  Offset,
  Ornament,
  PartMeasure,
  Spanner,
  SpannerKind,
  Voice,
  WordsStyle,
} from "../document/types";
import {
  at,
  counted,
  field,
  flag,
  identifier,
  keyed,
  list,
  maybe,
  measured,
  oneOf,
  text,
  whole,
} from "./reader";

const ARTICULATIONS: readonly Articulation[] = [
  "staccato",
  "staccatissimo",
  "accent",
  "strong-accent",
  "tenuto",
  "detached-legato",
  "breath-mark",
  "harmonic",
];

const DYNAMICS = [
  "pppp",
  "ppp",
  "pp",
  "p",
  "mp",
  "mf",
  "f",
  "ff",
  "fff",
  "ffff",
  "sf",
  "sfz",
  "fp",
  "rf",
  "rfz",
] as const;

const ORNAMENTS: readonly Ornament[] = [
  "trill",
  "turn",
  "inverted-turn",
  "delayed-turn",
  "mordent",
  "inverted-mordent",
  "shake",
  "schleifer",
];

const FERMATA_SHAPES = ["normal", "angled", "square"] as const;

const ARPEGGIOS = ["up", "down", "bracket"] as const;

const PLACEMENTS = ["above", "below"] as const;

const WORDS_STYLES = ["expression", "technique", "tempo"] as const;

/**
 * The lines a file may name.
 *
 * The union only ever grows, so a file written by an older editor still reads; a file that
 * uses a line this build does not know is a refusal rather than a line quietly dropped.
 */
const SPANNER_KINDS: readonly SpannerKind[] = [
  "slur",
  "crescendo",
  "diminuendo",
  "glissando",
  "pedal",
  "8va",
  "8vb",
  "15ma",
  "15mb",
];

/**
 * The marks a note may carry that older files do not mention.
 *
 * Each of these was added to the document after files had already been written, so absence
 * means "none of it" rather than a broken file -- the same bargain lyrics were given. The
 * format version does not move: an older editor ignores the field and a newer one defaults
 * it, and both read the same music.
 */
function readOrnaments(value: unknown, path: string): readonly Ornament[] {
  return value === undefined
    ? []
    : list((entry, where) => oneOf(ORNAMENTS, entry, where), value, path);
}

function readFermata(value: unknown, path: string): FermataShape | null {
  if (value === undefined) return null;
  return maybe((entry, where) => oneOf(FERMATA_SHAPES, entry, where), value, path);
}

/**
 * A hand-placed offset, or none.
 *
 * Absent means the element has not been moved, which is what every file written before
 * offsets existed meant by saying nothing. The format version does not move for it, as with
 * the other fields added this way: an older editor ignores the pair and draws the music
 * where its engraver puts it, which is the same music.
 */
function readOffset(value: unknown, path: string): Offset | null {
  if (value === undefined) return null;
  return maybe(
    (entry, where) => ({
      x: at(entry, "x", where, measured),
      y: at(entry, "y", where, measured),
    }),
    value,
    path,
  );
}

function readArpeggio(value: unknown, path: string): Arpeggio | null {
  if (value === undefined) return null;
  return maybe((entry, where) => oneOf(ARPEGGIOS, entry, where), value, path);
}

/** Tremolo beams. More than three cannot be struck through a stem and is a refusal. */
function readTremolo(value: unknown, path: string): number {
  if (value === undefined) return 0;
  const beams = counted(value, path);
  if (beams > 3) throw new RangeError(`${path} is more tremolo beams than a stem carries`);
  return beams;
}

export function readClef(value: unknown, path: string): Clef {
  return {
    sign: at(value, "sign", path, (v, p) =>
      oneOf(["G", "F", "C", "percussion", "TAB"] as const, v, p),
    ),
    line: at(value, "line", path, whole),
    octaveChange: at(value, "octaveChange", path, whole),
  };
}

function readTuplet(value: unknown, path: string): TupletRatio {
  const actual = at(value, "actual", path, counted);
  const normal = at(value, "normal", path, counted);
  if (actual < 1 || normal < 1) throw new RangeError(`${path} is not a ratio`);
  return { actual, normal };
}

export function readDuration(value: unknown, path: string): Duration {
  return {
    type: at(value, "type", path, (v, p) => oneOf(NOTE_TYPES, v, p)),
    dots: at(value, "dots", path, counted),
    tuplet: at(value, "tuplet", path, (v, p) => maybe(readTuplet, v, p)),
  };
}

function readPitch(value: unknown, path: string): NotePitch {
  const drumField = field(value, "drum", path);
  const tabField = field(value, "tab", path);
  return {
    step: at(value, "step", path, (v, p) => oneOf(STEPS, v, p)),
    alter: at(value, "alter", path, whole),
    octave: at(value, "octave", path, whole),
    accidental: at(value, "accidental", path, (v, p) =>
      oneOf(
        ["auto", "none", "natural", "sharp", "flat", "double-sharp", "double-flat"] as const,
        v,
        p,
      ),
    ),
    tieStart: at(value, "tieStart", path, flag),
    tieStop: at(value, "tieStop", path, flag),
    // A file written before drums existed says nothing here, and a head that names no
    // drum is an ordinary pitch -- which is what every head of those files was.
    drum: drumField === undefined ? null : maybe(text, drumField, `${path}.drum`),
    // A file written before tablature existed says nothing here, and a note with no
    // position is one nobody has said where to take -- which is every note of those files.
    tab: tabField === undefined ? null : maybe(readTab, tabField, `${path}.tab`),
  };
}

function readTab(value: unknown, path: string): TabPosition {
  return {
    string: at(value, "string", path, counted),
    fret: at(value, "fret", path, counted),
  };
}

function readLyric(value: unknown, path: string): Lyric {
  const verse = at(value, "verse", path, counted);
  if (verse < 1) throw new RangeError(`${path}.verse is not a verse number`);
  return {
    verse,
    text: at(value, "text", path, text),
    syllabic: at(value, "syllabic", path, (v, p) =>
      oneOf(["single", "begin", "middle", "end"] as const, v, p),
    ),
    extend: at(value, "extend", path, flag),
  };
}

/**
 * The sung text of a note, or none.
 *
 * Absence is allowed where every other field is required, because lyrics were added to the
 * document after the first files were written: a note that says nothing about them has
 * none, which is exactly what those files meant. The format version does not move for it --
 * an older editor ignores the field, a newer one defaults it, and both read the same music.
 */
function readLyrics(value: unknown, path: string): readonly Lyric[] {
  return value === undefined ? [] : list(readLyric, value, path);
}

function readEvent(value: unknown, path: string): MusicEvent {
  const id = at(value, "id", path, identifier<"event">);
  const duration = at(value, "duration", path, readDuration);
  const kind = at(value, "kind", path, (v, p) => oneOf(["note", "rest"] as const, v, p));

  const fermata = readFermata(field(value, "fermata", path), `${path}.fermata`);

  // Cues were added after the first files were written, so a note that says nothing about
  // one is not a cue -- which is what those files meant. The format version does not move
  // for it: an older editor ignores the field and a newer one defaults it.
  const cueField = field(value, "cue", path);
  const cue = cueField === undefined ? false : flag(cueField, `${path}.cue`);

  const offset = readOffset(field(value, "offset", path), `${path}.offset`);

  // Cross-staff notes were added the same way, and a file that says nothing about one
  // means the note sits on its voice's own staff, which is what every older file meant.
  const staffField = field(value, "staff", path);
  const staff = staffField === undefined ? null : maybe(counted, staffField, `${path}.staff`);

  if (kind === "rest") {
    return {
      id,
      kind,
      duration,
      fermata,
      cue,
      offset,
      staff,
      wholeMeasure: at(value, "wholeMeasure", path, flag),
    };
  }
  return {
    id,
    kind,
    duration,
    pitches: at(value, "pitches", path, (v, p) => list(readPitch, v, p)),
    stem: at(value, "stem", path, (v, p) => oneOf(["auto", "up", "down"] as const, v, p)),
    beam: at(value, "beam", path, (v, p) =>
      oneOf(["auto", "none", "begin", "continue", "end"] as const, v, p),
    ),
    articulations: at(value, "articulations", path, (v, p) =>
      list((entry, where) => oneOf(ARTICULATIONS, entry, where), v, p),
    ),
    ornaments: readOrnaments(field(value, "ornaments", path), `${path}.ornaments`),
    arpeggio: readArpeggio(field(value, "arpeggio", path), `${path}.arpeggio`),
    tremolo: readTremolo(field(value, "tremolo", path), `${path}.tremolo`),
    fermata,
    cue,
    offset,
    staff,
    grace: at(value, "grace", path, (v, p) =>
      maybe((entry, where) => oneOf(["acciaccatura", "appoggiatura"] as const, entry, where), v, p),
    ),
    lyrics: readLyrics(field(value, "lyrics", path), `${path}.lyrics`),
  };
}

function readVoice(value: unknown, path: string): Voice {
  // Ossias were added after files existed, and a file that says nothing about one holds a
  // line that is played rather than offered -- which is what every older file held. The
  // format version does not move for it, as with the other fields added this way.
  const ossiaField = field(value, "ossia", path);
  return {
    id: at(value, "id", path, identifier<"voice">),
    staff: at(value, "staff", path, counted),
    events: at(value, "events", path, (v, p) => list(readEvent, v, p)),
    ossia: ossiaField === undefined ? false : flag(ossiaField, `${path}.ossia`),
  };
}

/**
 * How a words direction is written, for a file that does not say.
 *
 * Every word in a file written before styles existed was drawn upright over the staff,
 * which is what `technique` is, so that is what those files meant. The format version does
 * not move for it, as with the other fields added this way.
 */
function readWordsStyle(value: unknown, path: string): WordsStyle {
  return value === undefined ? "technique" : oneOf(WORDS_STYLES, value, path);
}

function readDirection(value: unknown, path: string): Direction {
  const kind = at(value, "content", path, (v, p) =>
    at(v, "kind", p, (entry, where) => oneOf(["dynamic", "words"] as const, entry, where)),
  );
  const content = at(value, "content", path, (v, p) =>
    kind === "dynamic"
      ? ({ kind, mark: at(v, "mark", p, (entry, where) => oneOf(DYNAMICS, entry, where)) } as const)
      : ({
          kind,
          text: at(v, "text", p, text),
          style: readWordsStyle(field(v, "style", p), `${p}.style`),
        } as const),
  );

  return {
    id: at(value, "id", path, identifier<"direction">),
    anchor: at(value, "anchor", path, identifier<"event">),
    placement: at(value, "placement", path, (v, p) => oneOf(PLACEMENTS, v, p)),
    content,
  };
}

export function readSpanner(value: unknown, path: string): Spanner {
  return {
    id: at(value, "id", path, identifier<"spanner">),
    kind: at(value, "kind", path, (v, p) => oneOf(SPANNER_KINDS, v, p)),
    from: at(value, "from", path, identifier<"event">),
    to: at(value, "to", path, identifier<"event">),
    placement: at(value, "placement", path, (v, p) => oneOf(PLACEMENTS, v, p)),
  };
}

/** How far an instrument is written from where it sounds. Shared with the part's own. */
export function readTransposition(value: unknown, path: string): Transposition {
  return {
    chromatic: at(value, "chromatic", path, whole),
    diatonic: at(value, "diatonic", path, whole),
    octaveChange: at(value, "octaveChange", path, whole),
  };
}

function readInstrumentChange(value: unknown, path: string): InstrumentChange {
  return {
    name: at(value, "name", path, text),
    sound: at(value, "sound", path, (v, p) => maybe(text, v, p)),
    midiProgram: at(value, "midiProgram", path, (v, p) => maybe(whole, v, p)),
    transposition: at(value, "transposition", path, readTransposition),
  };
}

/**
 * One bar of one part.
 *
 * `clefChanges` is keyed by staff index, and JSON has only string keys, so the numbers come
 * back as `"0"` and `"1"` and are turned back into numbers here. A key that is not a whole
 * number is a refusal: a clef change on staff `"left hand"` addresses nothing.
 */
export function readPartMeasure(value: unknown, path: string): PartMeasure {
  const changes = at(value, "clefChanges", path, (v, p) => keyed(readClef, v, p));
  const taken = field(value, "instrument", path);
  const harmonyField = field(value, "harmonies", path);
  const figureField = field(value, "figures", path);
  const clefChanges: Record<number, Clef> = {};
  for (const [staff, clef] of Object.entries(changes)) {
    const index = Number(staff);
    if (!Number.isInteger(index) || index < 0) {
      throw new RangeError(`${path}.clefChanges.${staff} is not a staff`);
    }
    clefChanges[index] = clef;
  }

  return {
    voices: at(value, "voices", path, (v, p) => list(readVoice, v, p)),
    // A file written before a player could pick another instrument up says nothing here,
    // and a bar that says nothing is a bar the part plays on through -- which is what
    // every bar of those files was.
    instrument:
      taken === undefined ? null : maybe(readInstrumentChange, taken, `${path}.instrument`),
    clefChanges,
    directions: at(value, "directions", path, (v, p) => list(readDirection, v, p)),
    // A file written before chord symbols existed says nothing here, and a bar with no
    // harmony over it is what every bar of those files was.
    harmonies:
      harmonyField === undefined ? [] : list(readHarmony, harmonyField, `${path}.harmonies`),
    // Likewise for figured bass: absent means a bar nobody wrote a figure under.
    figures: figureField === undefined ? [] : list(readFigures, figureField, `${path}.figures`),
  };
}

/**
 * One chord symbol.
 *
 * The kind is checked against the table rather than taken as text, because a kind the
 * editor cannot name is one it cannot draw or transpose, and a file that carries one is
 * better refused at the door than opened into a score with a silent hole in it.
 */
function readHarmony(value: unknown, path: string): Harmony {
  const bass = field(value, "bass", path);
  return {
    id: at(value, "id", path, identifier<"harmony">),
    onset: at(value, "onset", path, whole),
    root: at(value, "root", path, readChordRoot),
    kind: at(value, "kind", path, (v, p) => oneOf(HARMONY_KINDS, v, p)),
    bass: bass === undefined ? null : maybe(readChordRoot, bass, `${path}.bass`),
  };
}

/**
 * One stack of figures under a bass note.
 *
 * `parenthesized` is a bar-level fact about the whole stack, so it is read once here rather
 * than per numeral, and an older file that has no such field means a stack nobody bracketed.
 */
function readFigures(value: unknown, path: string): FiguredBass {
  const bracket = field(value, "parenthesized", path);
  return {
    id: at(value, "id", path, identifier<"figure">),
    onset: at(value, "onset", path, whole),
    figures: at(value, "figures", path, (v, p) => list(readFigure, v, p)),
    parenthesized: bracket === undefined ? false : flag(bracket, `${path}.parenthesized`),
  };
}

function readFigure(value: unknown, path: string): Figure {
  const mark = (v: unknown, p: string): FigureMark => oneOf(FIGURE_MARKS, v, p);
  const number = field(value, "number", path);
  return {
    prefix: maybe(mark, field(value, "prefix", path), `${path}.prefix`),
    number: number === undefined || number === null ? null : whole(number, `${path}.number`),
    suffix: maybe(mark, field(value, "suffix", path), `${path}.suffix`),
    extend: at(value, "extend", path, flag),
  };
}

function readChordRoot(value: unknown, path: string): ChordRoot {
  return {
    step: at(value, "step", path, (v, p) => oneOf(STEPS, v, p)),
    alter: at(value, "alter", path, whole),
  };
}
